/**
 * "Next up" — what the outreach loop will send when, grouped by Berlin day.
 *
 * The server replays the loop's own rules (office hours at the recipient,
 * the warm-up limit, follow-ups first, a few minutes between sends), so a
 * time here is an estimate that moves with every reply, bounce or wave
 * update. The first days are open; later days and the emails without a time
 * fold into chips. Presentational.
 */
import { Fragment, useState } from "react";

import type { PlanItem, SendPlan } from "../../api/b2g";
import { STEP_LABEL } from "../../lib/b2g";
import {
  berlinDay,
  berlinTime,
  dayLabel,
  defaultOpenDays,
  groupByDay,
  holdText,
  localTime,
  planReason,
} from "../../lib/sendPlan";
import { CountryLabel } from "./CountryLabel";

const HELD = "held";

export function SendPlanPanel({ plan, onOpenWave }: { plan: SendPlan; onOpenWave: (id: string) => void }) {
  const today = berlinDay(new Date());
  const { days, held } = groupByDay(plan.items);
  const defaults = defaultOpenDays(days);
  // Toggles on top of the defaults, so a day keeps its state while the plan refreshes.
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  const isOpen = (key: string) => (key === HELD ? false : defaults.has(key)) !== toggled.has(key);
  const toggle = (key: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const closed = days.filter((d) => !isOpen(d.day));
  const openDays = days.filter((d) => isOpen(d.day));

  return (
    <div className="panel panel--flush mb-6">
      <div className="panel-header">
        <h3>Next up</h3>
        <span className="text-xs text-ink-low">
          Estimate – replies, bounces and wave updates move it. Follow-ups go only without a reply.
        </span>
      </div>
      {plan.sending_disabled ? (
        <div role="status" className="callout callout--warn m-3 text-sm">
          Sending is switched off on the server (OUTREACH_DISABLED) – nothing below goes out until it is on again.
        </div>
      ) : null}
      {plan.items.length === 0 ? (
        <div className="panel-body text-sm text-ink-low">Nothing planned – no running wave has an email left to send.</div>
      ) : (
        <>
          {openDays.length || isOpen(HELD) ? (
            <div className="overflow-x-auto">
              <table className="table">
                <tbody>
                  {openDays.map((d) => (
                    <Fragment key={d.day}>
                      <DayHeader label={dayLabel(d.day, today)} count={d.items.length} onFold={() => toggle(d.day)} />
                      {d.items.map((item) => (
                        <PlanRow key={item.message_id} item={item} onOpenWave={onOpenWave} />
                      ))}
                    </Fragment>
                  ))}
                  {isOpen(HELD) ? (
                    <>
                      <DayHeader label="Without a time" count={held.length} onFold={() => toggle(HELD)} />
                      {held.map((item) => (
                        <PlanRow
                          key={item.message_id}
                          item={item}
                          onOpenWave={onOpenWave}
                          hold={holdText(item, plan.horizon_days)}
                        />
                      ))}
                    </>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
          {closed.length || (held.length && !isOpen(HELD)) ? (
            <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t border-rule">
              {closed.map((d) => (
                <button key={d.day} type="button" className="btn btn-sm btn-ghost" onClick={() => toggle(d.day)}>
                  ▸ {dayLabel(d.day, today)} · <span className="tnum">{d.items.length}</span>
                </button>
              ))}
              {held.length && !isOpen(HELD) ? (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => toggle(HELD)}>
                  ▸ Without a time · <span className="tnum">{held.length}</span>
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function DayHeader({ label, count, onFold }: { label: string; count: number; onFold: () => void }) {
  return (
    <tr className="bg-surface-tint">
      <td colSpan={6} className="py-1.5">
        <button type="button" className="text-xs font-semibold text-ink-medium" onClick={onFold}>
          ▾ {label} · <span className="tnum">{count}</span>
        </button>
      </td>
    </tr>
  );
}

function PlanRow({ item, onOpenWave, hold }: { item: PlanItem; onOpenWave: (id: string) => void; hold?: string }) {
  const local = item.at ? localTime(item.at, item.time_zone) : null;
  return (
    <tr className="align-top">
      <td className="whitespace-nowrap">
        {item.at ? (
          <>
            <span className="font-mono tnum">≈ {berlinTime(item.at)}</span>
            {local ? <div className="text-xs text-ink-low tnum">{local} local</div> : null}
          </>
        ) : (
          <span className="text-ink-low">–</span>
        )}
      </td>
      <td>
        <div className="font-semibold text-ink">{item.recipient_name ?? item.recipient_email}</div>
        {item.company_name ?? item.company_id ? (
          <div className="text-xs text-ink-low">{item.company_name ?? item.company_id}</div>
        ) : null}
      </td>
      <td className="text-sm">
        <CountryLabel iso2={item.market_iso2} />
      </td>
      <td className="text-sm whitespace-nowrap">{STEP_LABEL[item.step] ?? `Step ${item.step}`}</td>
      <td className="text-sm max-w-xs">
        <button type="button" className="link text-left truncate max-w-full" title={item.wave_title} onClick={() => onOpenWave(item.wave_id)}>
          {item.wave_title}
        </button>
      </td>
      <td className="text-xs text-ink-low">{hold ?? planReason(item)}</td>
    </tr>
  );
}
